import { IconButton, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import AlertFormProvider, {
  useAlertForm
} from 'common/components/ParameterAlertForm/AlertFormProvider'
import Devices from 'common/components/ParameterAlertForm/steps/Devices/Devices'
import Parameters from 'common/components/ParameterAlertForm/steps/Parameters/Parameters'
import Settings from 'common/components/ParameterAlertForm/steps/Settings/Settings'
import Values from 'common/components/ParameterAlertForm/steps/Values/Values'
import getTimingStateFromPeriods from 'common/components/ParameterAlertForm/TimeWindow/getTimingStateFromPeriods'
import styles from 'common/components/ParameterAlertForm/ParameterAlertForm.module.scss'
import DataTypes from 'constants/DataTypes'
import * as DataUtils from 'utils/data'
import { getCompleteMeasurementUnits } from 'utils/dataUnitsDefault'
import { getProjectAlertRecipients } from 'utils/alertRecipients'

const ActiveStep = ({ step, children }) => {
  const {
    state: { activeTab }
  } = useAlertForm()
  return activeTab === step ? children : null
}

const getUserData = user => (user?.toJS ? user.toJS() : user || {})

export const getEditAlertInitialValues = ({ alarm = {}, systems = [], user, projects, offset }) => {
  const userData = getUserData(user)
  const measurementUnits = getCompleteMeasurementUnits(userData.measurementUnits || {})
  const meta = alarm.Meta || {}
  const types = Object.keys(meta)
  const typesInput = types.reduce((values, type) => {
    const displayType = type === 'tempLow' || type === 'tempHigh' ? DataTypes.TEMPERATURE : type
    values[type] = DataUtils.formatData(meta[type], displayType, measurementUnits)
    return values
  }, {})

  const availableDeviceIds = new Set(systems.map(system => system._id))
  const selectedDevices = Array.isArray(alarm.DeviceIds)
    ? alarm.DeviceIds.filter(deviceId => availableDeviceIds.has(deviceId))
    : []

  const emails = {}
  if (Array.isArray(alarm.emailRecipients)) {
    alarm.emailRecipients.forEach(email => {
      if (typeof email === 'string' && email) emails[email] = true
    })
  } else {
    const { ownRecipient } = getProjectAlertRecipients(projects, userData.email)
    if (ownRecipient) emails[ownRecipient.email] = true
  }

  const alarmMailHours = alarm.alarmMailHours || {}

  return {
    types,
    typesInput,
    selectedDevices,
    existingDeviceIds: selectedDevices,
    alarmName: alarm.name || '',
    emails,
    occupancyDays: alarmMailHours.days || {},
    timing: getTimingStateFromPeriods({
      period1: alarmMailHours.period1,
      period2: alarmMailHours.period2,
      auxPeriod: alarmMailHours.auxPeriod,
      auxPeriod2: alarmMailHours.auxPeriod2,
      offset
    })
  }
}

const EditParameterAlertModalForm = props => {
  const {
    onSubmit,
    user,
    translate,
    systems,
    systemsByGroup,
    parameterAlarmParamsBySystem,
    isSubmitting,
    isAddingEmail,
    submitError,
    addAlertRecipient,
    onDeleteAlertRecipient,
    projectId,
    projects,
    alarm
  } = props

  // KNOWN BUG (bounded, do not fix here for now):
  // When we hydrate the Edit form, we decode the saved UTC periods using the
  // browser's CURRENT offset, not the offset they were saved with
  // (alarm.alarmMailHours.offset). If those differ (summer/winter time change,
  // or a user in another timezone), the time window shown in Edit can be off
  // by about 1 hour.
  // Why it stays small: it does not affect "all day" alarms, because they do not
  // convert hours. Saving without changing the hours also does not change the
  // stored UTC value, only the offset field. The schedule only breaks if the user
  // manually "fixes" the wrong time they see and then saves.
  // So if a client reports a shifted time window on an alarm that has a schedule,
  // this is the cause. Future fix:
  //   const offset = alarm?.alarmMailHours?.offset ?? new Date().getTimezoneOffset()
  const offset = new Date().getTimezoneOffset()
  const initialValues = getEditAlertInitialValues({
    alarm,
    systems,
    user,
    projects,
    offset
  })

  return (
    <div className={styles.modalContainer}>
      <IconButton
        className={styles.closeModalButton}
        onClick={props.hideModal}
        aria-label='close'
      >
        <CloseIcon />
      </IconButton>
      <Typography className={styles.header}>{translate('alertsPage.editAlert')}</Typography>

      <div className={styles.modal}>
        <AlertFormProvider
          formMode='edit'
          initialValues={initialValues}
          onSubmit={onSubmit}
          modalActions={{
            addAlertRecipient,
            onDeleteAlertRecipient,
            hideModal: props.hideModal
          }}
          config={{
            translate,
            user,
            systems,
            systemsByGroup,
            parameterAlarmParamsBySystem,
            isSubmitting,
            isAddingEmail,
            submitError,
            projectId,
            projects
          }}
        >
          <form
            noValidate
            onKeyDown={event => {
              if (event.key === 'Enter') event.preventDefault()
            }}
          >
            <ActiveStep step={0}>
              <Parameters />
            </ActiveStep>
            <ActiveStep step={1}>
              <Values />
            </ActiveStep>
            <ActiveStep step={2}>
              <Devices />
            </ActiveStep>
            <Settings />
          </form>
        </AlertFormProvider>
      </div>
    </div>
  )
}

export default EditParameterAlertModalForm
